import * as React from 'react'
import { ReactElement, useMemo, useState } from 'react'
import Card from '../../../../components/Card'
import { FontWeights, Stack, Text } from '@fluentui/react'
import LabelValue from '../../../LabelValue/LabelValue'
import TimeText from '../../../Text/Time/TimeText'
import MoneyText from '../../../Text/Currency/MoneyText'
import { getFilePartyFromMeta, InvoiceProps, PriceVat } from './InvoiceForm'
import { useFormikContext } from 'formik'
import { InvoiceFinalList, InvoiceMutationDto, InvoiceStatus, InvoiceType, LineItemType } from '../../../../config/finance/invoiceConfig'
import { useHistory } from 'react-router-dom'
import DistanceText from '../../../Text/Distance/DistanceText'
import { stealthClick } from '../../../StealthLink'
import useAxios from '../../../../hooks/useAxios'
import useCurrency from '../../../../hooks/useCurrency'
import { reducePrices } from '../../../../helpers/priceHelper'
import { formatDecimals, parseNumber, reduceFormattedDuration } from '../../../../helpers/numberHelper'
import { TimeListItemDto, TravelListItemDto } from '../../../../config/registrationConfig'
import { isAppContext } from '../../../../helpers/appHelper'
import { AppContext } from '../../../../config/appConfig'
import useComposeInvoiceOutlookMessage from '../../../../hooks/finance/invoice/useComposeInvoiceOutlookMessage'
import { QueueStatus } from '../../../../hooks/useQueueStateHandler'
import DisconnectInvoiceButton from '../../../Button/DisconnectInvoiceButton'
import { Button, Link } from '@fluentui/react-components'
import {
    ArrowLeftFilled,
    ArrowLeftRegular,
    ArrowResetFilled,
    ArrowResetRegular,
    bundleIcon,
    CheckmarkCircleFilled,
    CheckmarkCircleRegular,
    DeleteFilled,
    DeleteRegular,
    OpenFilled,
    OpenRegular,
    PrintFilled,
    PrintRegular,
    SaveArrowRightFilled,
    SaveArrowRightRegular,
    SaveEditFilled,
    SaveEditRegular,
} from '@fluentui/react-icons'
import useHost from '../../../../hooks/useHost'
import useUnits from '../../../../hooks/useUnits'
import { useTranslation } from 'react-i18next'
import { useSettings } from '../../../../hooks/useSettings'
import { DocumentOpenType } from '../../../../context/GlobalContext'
import SubmittingButton from '../../../../v2/components/Button/SubmittingButton'

type Props = InvoiceProps

const BackIcon = bundleIcon(ArrowLeftFilled, ArrowLeftRegular)
const CancelIcon = bundleIcon(ArrowResetFilled, ArrowResetRegular)
const DeleteIcon = bundleIcon(DeleteFilled, DeleteRegular)
const DoneIcon = bundleIcon(CheckmarkCircleFilled, CheckmarkCircleRegular)
const DraftIcon = bundleIcon(SaveEditFilled, SaveEditRegular)
const FinalIcon = bundleIcon(SaveArrowRightFilled, SaveArrowRightRegular)
const OpenIcon = bundleIcon(OpenFilled, OpenRegular)
const PrintIcon = bundleIcon(PrintFilled, PrintRegular)

export default function HeaderCard({
    invoice,
    meta,
    hasParties,
    isEditable,
    lineItemGroupPrices,
    lineItemEntities,
    persist,
    generatePdf,
}: Props): ReactElement {
    const { t: translate } = useTranslation()
    const axios = useAxios()
    const history = useHistory()
    const { settings } = useSettings()

    const formikContext = useFormikContext<InvoiceMutationDto>()
    const composeOutlookMessage = useComposeInvoiceOutlookMessage()

    const [isSubmitting, setIsSubmitting] = useState<boolean>()
    const { values, setStatus, resetForm, dirty } = formikContext
    const { currency } = useCurrency(values.currency)
    const host = useHost()
    const { units } = useUnits()

    const unit = useMemo(() => (units || []).find(unit => unit.key === values.unit), [units, values.unit])

    const fileParties = useMemo(() => meta.data.files?.flatMap(file => file.fileParties) || [], [meta.data.files])

    const price = useMemo<PriceVat>(
        () =>
            [...lineItemGroupPrices.values()].reduce(reducePrices, {
                subtotal: 0,
                vat: [],
                total: 0,
            }),
        [lineItemGroupPrices],
    )
    const outstandingPrice = useMemo<number>(
        () => parseNumber(formatDecimals(invoice?.outstandingPrice?.total ?? 0, 2), 'en'),
        [invoice?.price, invoice?.outstandingPrice],
    )

    const paidPrice = useMemo<number>(() => {
        return parseNumber(formatDecimals(invoice?.price?.total, 2), 'en') - outstandingPrice
    }, [invoice?.price, outstandingPrice])

    const duration = useMemo<string>(
        () => reduceFormattedDuration(lineItemEntities.selection.get(LineItemType.Time) as TimeListItemDto[]),
        [lineItemEntities.selection.get(LineItemType.Time)],
    )
    const distance = useMemo<string>(
        () =>
            lineItemEntities.selection
                .get(LineItemType.Travel)
                .map((entity: TravelListItemDto) => parseNumber(entity.distance || 0, 'en'))
                .reduce((acc: number, distance: number) => acc + distance, 0)
                .toFixed(0),
        [lineItemEntities.selection.get(LineItemType.Travel)],
    )
    const debitGlaNumber = useMemo<string>(() => meta.data.glas?.find(gla => gla.unit === values.unit)?.debit?.code, [meta.data])
    const debtorNumber = useMemo<string>(
        () =>
            getFilePartyFromMeta(values.debtorPivotId, undefined, undefined, fileParties)?.[0]?.externals.find(
                external => external.unit === values.unit,
            )?.code,
        [values.debtorPivotId, values.unit, fileParties],
    )

    const transactionUrl = useMemo(
        () =>
            unit?.key &&
            values.transaction?.id &&
            `${process.env.API_HOST}/accounting/open/transaction/${host.RowKey}/${unit.key}/${values.transaction?.id}`,
        [unit, values.unit, invoice, values.transaction],
    )

    return (
        <Card>
            <Stack horizontal tokens={{ childrenGap: 's2', maxWidth: '100%' }}>
                <Stack grow>
                    <Stack tokens={{ childrenGap: 0 }}>
                        <LabelValue value={getFilePartyFromMeta(values.clientPivotId, undefined, undefined, fileParties)?.[0]?.displayName} />
                    </Stack>

                    <Stack horizontal wrap tokens={{ childrenGap: 'l2' }}>
                        {duration && (
                            <LabelValue label={translate('invoice.edit.form.header.duration')}>
                                <TimeText value={duration} />
                            </LabelValue>
                        )}
                        {distance && (
                            <LabelValue label={translate('invoice.edit.form.header.distance')}>
                                <DistanceText value={distance} />
                            </LabelValue>
                        )}
                        {currency && (
                            <LabelValue label={translate('invoice.edit.form.header.subtotal')}>
                                <MoneyText amount={price.subtotal} currency={currency} />
                            </LabelValue>
                        )}
                        {InvoiceFinalList.includes(values.status) && currency && (
                            <LabelValue label={translate('invoice.edit.form.header.vat')}>
                                <MoneyText
                                    amount={invoice?.price?.vat?.map(a => parseNumber(a.value, 'en')).reduce((a, b) => a + b, 0)}
                                    currency={currency}
                                />
                            </LabelValue>
                        )}
                        {InvoiceFinalList.includes(values.status) && currency && (
                            <LabelValue label={translate('invoice.edit.form.header.total')} valueStyles={{ fontWeight: FontWeights.bold }}>
                                <MoneyText amount={invoice?.price?.total} currency={currency} />
                            </LabelValue>
                        )}
                        {InvoiceFinalList.includes(values.status) && currency && !!paidPrice && (
                            <LabelValue label={translate('invoice.edit.form.header.paidtotal')}>
                                <MoneyText amount={paidPrice} currency={currency} />
                            </LabelValue>
                        )}
                        {InvoiceFinalList.includes(values.status) && currency && !!outstandingPrice && (
                            <LabelValue label={translate('invoice.edit.form.header.outstandingtotal')}>
                                <MoneyText amount={outstandingPrice} currency={currency} />
                            </LabelValue>
                        )}
                    </Stack>

                    {hasParties && (
                        <Stack horizontal wrap tokens={{ childrenGap: 'l2' }}>
                            <LabelValue label={translate('invoice.edit.form.header.number')} value={values.number} />
                            <LabelValue
                                label={translate('invoice.edit.form.header.term')}
                                value={values.term}
                                prefix={translate('invoice.edit.form.header.term_prefix')}
                                suffix={translate('invoice.edit.form.header.term_suffix')}
                            />
                            <LabelValue label={translate('invoice.edit.form.header.recipientreference.label')} value={values.recipientReference} />
                            <LabelValue label={translate('invoice.edit.form.header.recipientemail')} value={values.recipientEmail} />
                            {invoice?.paymentLink && (
                                <LabelValue
                                    label={translate('invoice.edit.form.header.paymentlink')}
                                    value={translate(`invoice.paymentlink.status_${invoice?.paymentLink?.status?.toLowerCase()}`)}
                                />
                            )}
                        </Stack>
                    )}
                    <Stack horizontal wrap tokens={{ childrenGap: 'l2' }}>
                        {debitGlaNumber && (
                            <LabelValue label={translate('invoice.edit.form.header.debitglanumber')} valueStyles={{ fontWeight: FontWeights.bold }}>
                                <Text variant="mediumPlus">{debitGlaNumber}</Text>
                            </LabelValue>
                        )}
                        {debtorNumber && <LabelValue label={translate('invoice.edit.form.header.debtornumber')} value={debtorNumber} />}
                        {transactionUrl && (
                            <LabelValue label={translate('invoice.edit.form.header.transaction.code')}>
                                <Link id={values.transaction.id} href={transactionUrl} target={'_blank'}>
                                    {values.transaction.code} <OpenIcon />
                                </Link>
                            </LabelValue>
                        )}
                    </Stack>
                    {values.invoiceApprovalId && (
                        <Stack horizontal wrap tokens={{ childrenGap: 'l2' }}>
                            <LabelValue label={translate('invoice.edit.form.header.invoiceapproval.label')}>
                                <Link onClick={() => history.push(`/invoice-approval/${values.invoiceApprovalId}`)}>
                                    {translate('invoice.edit.form.header.invoiceapproval.action')} <OpenIcon />
                                </Link>
                            </LabelValue>
                        </Stack>
                    )}
                </Stack>
                <Stack tokens={{ childrenGap: 's1' }}>
                    {[InvoiceStatus.Final, InvoiceStatus.Settled, InvoiceStatus.Credited].includes(values.status) && (
                        <SubmittingButton
                            appearance={'subtle'}
                            icon={<DoneIcon />}
                            isSubmitting={isSubmitting}
                            setIsSubmitting={setIsSubmitting}
                            disabled={!hasParties}
                            onClick={() => {
                                if (invoice) {
                                    setStatus(translate('invoice.edit.info.done'))
                                    return axios.post(`/finance/invoice/${invoice.id}/done`).finally(() => history.push('/invoice'))
                                }

                                return Promise.resolve()
                            }}
                        >
                            {translate('invoice.status.done')}
                        </SubmittingButton>
                    )}
                    {!isEditable && invoice?.id && (
                        <DisconnectInvoiceButton
                            disabled={!hasParties || (invoice?.credit?.length > 0 && InvoiceStatus.Settled === values.status) || isSubmitting}
                            onConfirm={() => {
                                if (isSubmitting) {
                                    return
                                }

                                setIsSubmitting(true)
                                setStatus(translate('invoice.edit.info.unlock'))
                                axios.post(`/finance/invoice/${invoice.id}/unlock`).finally(() => {
                                    setIsSubmitting(false)
                                    resetForm()
                                })
                            }}
                        />
                    )}
                    {isEditable && (
                        <SubmittingButton
                            appearance={'subtle'}
                            icon={<FinalIcon />}
                            isSubmitting={isSubmitting}
                            setIsSubmitting={setIsSubmitting}
                            disabled={!hasParties}
                            onClick={() =>
                                persist(InvoiceStatus.Final, data => {
                                    history.push('/invoice')
                                    generatePdf(data, {
                                        [QueueStatus.Finished]: data => {
                                            if (isAppContext([AppContext.Read, AppContext.Module])) {
                                                composeOutlookMessage(data.context.data.invoiceId, values.recipientEmail, data.status.driveItem)
                                            }
                                        },
                                    })
                                })
                            }
                        >
                            {translate('invoice.status.final')}
                        </SubmittingButton>
                    )}
                    {[InvoiceStatus.New, InvoiceStatus.Draft].includes(values.status) && (
                        <SubmittingButton
                            appearance={'subtle'}
                            icon={<DraftIcon />}
                            isSubmitting={isSubmitting}
                            setIsSubmitting={setIsSubmitting}
                            disabled={!dirty || !hasParties}
                            onClick={() =>
                                persist(InvoiceStatus.Draft, data => {
                                    history.push('/invoice')
                                    generatePdf(data)
                                })
                            }
                        >
                            {translate('invoice.status.draft')}
                        </SubmittingButton>
                    )}
                    {![InvoiceType.Split].includes(values.type) && (
                        <SubmittingButton
                            appearance={'subtle'}
                            icon={<PrintIcon />}
                            isSubmitting={isSubmitting}
                            setIsSubmitting={setIsSubmitting}
                            disabled={!hasParties}
                            onClick={() => {
                                if (invoice?.pdfId && !dirty) {
                                    // If we already have a PDF loaded, show this one.
                                    return stealthClick({ id: invoice.pdfId, documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open }, axios)
                                } else if ([InvoiceStatus.New, InvoiceStatus.Draft].includes(values.status)) {
                                    return generatePdf(
                                        { invoiceId: invoice.id, invoiceNumber: invoice.number },
                                        {
                                            [QueueStatus.Finished]: data => {
                                                stealthClick(
                                                    {
                                                        id: data.status.driveItem.id,
                                                        item: data.status.driveItem,
                                                        documentsOpen: settings.documentsOpen ?? DocumentOpenType.Open,
                                                    },
                                                    axios,
                                                )
                                                if (!invoice && data.context.data.invoiceId) {
                                                    history.replace(`/invoice/${data.context.data.invoiceId}/edit`)
                                                } else {
                                                    // Make sure to reload the form such that the invoice is reloaded into
                                                    // memory.
                                                    resetForm()
                                                }
                                            },
                                        },
                                    )
                                }
                                return Promise.resolve()
                            }}
                        >
                            {translate('action.print')}
                        </SubmittingButton>
                    )}
                    {[InvoiceStatus.Draft].includes(values.status) && invoice?.id && (
                        <SubmittingButton
                            appearance={'subtle'}
                            icon={<DeleteIcon />}
                            isSubmitting={isSubmitting}
                            setIsSubmitting={setIsSubmitting}
                            disabled={!hasParties}
                            onClick={() => {
                                setStatus(translate('invoice.edit.info.delete'))
                                return axios.delete(`/finance/invoice/${invoice.id}`).finally(() => history.push('/invoice'))
                            }}
                        >
                            {translate('action.delete')}
                        </SubmittingButton>
                    )}
                    <Button
                        appearance={'subtle'}
                        icon={isEditable && dirty ? <CancelIcon /> : <BackIcon />}
                        disabled={!hasParties || isSubmitting}
                        onClick={() => {
                            if (isSubmitting) {
                                return
                            }

                            setIsSubmitting(true)
                            history.push('/invoice')
                        }}
                    >
                        {isEditable && dirty ? translate('action.cancel') : translate('action.back')}
                    </Button>
                </Stack>
            </Stack>
        </Card>
    )
}
